import { rotuloComNumero } from './rotulos';

/**
 * Esta OS tem diário REJEITADO esperando correção do campo?
 *
 * A REGRA, e por que ela não é `rdo_aprovado === false`: esse campo significa "a OS foi
 * FINALIZADA" (todos os diários aprovados), não "o diário foi reprovado". Ele nasce `false`
 * e só vira `true` no fechamento — então TODA OS em andamento casava com a regra antiga, e o
 * app de campo marcava como "RDO rejeitado" OS que nunca teve rejeição nenhuma.
 *
 * O sinal honesto é o contador de rejeitados, mantido a partir dos diários reais
 * (`backend/routes/rdos_common.recontar_rdos_demanda`). `rdo_rejeitado_ref` diz QUAL diário é.
 *
 * Um lugar só: a mesma pergunta era feita em três telas do app de campo, e a divergência
 * entre elas é o tipo de coisa que ninguém percebe até o usuário reclamar.
 */
export const temDiarioRejeitado = (demanda) => (
  (Number(demanda?.rdos_rejeitados) || 0) > 0 || !!demanda?.rdo_rejeitado_ref
);

const STATUS_DO_DIARIO = ['aprovado', 'rejeitado', 'pendente'];

/**
 * O status DE UM DIÁRIO — a MESMA régua de `backend/routes/rdos_common.py::status_efetivo_rdo`.
 * Vale o `status_rdo` que o servidor já calculou; sem ele, o `status` do próprio diário, se for um
 * dos três; e o diário LEGADO (anterior à OS com vários diários, sem status próprio) herda o da
 * OS: aprovado se a OS foi aprovada, pendente se não. Qualquer outro valor não é status de
 * diário e cai no legado — como no servidor, para a tela não mostrar um terceiro número.
 */
export const statusDoRdo = (rdo, statusDaOs) => {
  const r = rdo || {};
  if (STATUS_DO_DIARIO.includes(r.status_rdo)) return r.status_rdo;
  if (STATUS_DO_DIARIO.includes(r.status)) return r.status;
  return (statusDaOs || r.status_demanda) === 'approved' ? 'aprovado' : 'pendente';
};

/**
 * `2026-08-06` → `06/08/2026`. Data que o campo LÊ, não a que o banco grava.
 *
 * O `T12:00:00` não é enfeite: `new Date('2026-08-06')` é interpretado como UTC e, no fuso do
 * Brasil, volta como dia 5. Meio-dia é o único horário que sobrevive a qualquer fuso.
 */
export const dataBR = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('pt-BR');
};

/**
 * QUAL diário está reprovado, em texto — para o banner, o card e o modal dizerem a MESMA coisa.
 *
 * A regra estava escrita três vezes e já tinha divergido: o banner nomeava o dia e levava ao
 * diário certo, o card estampava "RDO Reprovado" por cima do status da OS inteira, e o botão
 * do modal abria o diário de HOJE. O colaborador lia "reprovado", clicava, caía num formulário
 * em branco e refazia o dia errado — enquanto o gestor via o diário novo como "aguardando".
 * Os dois olhando a mesma OS e lendo coisas opostas.
 *
 * Devolve `null` quando não há rejeição. `rdoId` é `null` em OS antiga, anterior ao backfill de
 * `rdo_rejeitado_ref` — aí o texto cai para a contagem, nunca some.
 */
export const rotuloDiarioRejeitado = (demanda) => {
  if (!temDiarioRejeitado(demanda)) return null;
  const ref = demanda?.rdo_rejeitado_ref || null;
  const dia = ref?.data
    ? new Date(String(ref.data).slice(0, 10) + 'T12:00:00')
      .toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    : null;
  const quantos = Number(demanda?.rdos_rejeitados) || 1;
  // "VEIO NÚMERO?" — não "veio número DIFERENTE DE ZERO?". Ver `utils/rotulos.js`: a régua é
  // uma só porque a mesma condição estava escrita em oito lugares do `src`, e este rótulo
  // alimenta o banner, o chip e o modal do app de campo ao mesmo tempo.
  const nome = rotuloComNumero('RDO', ref?.numero_rdo);
  return {
    rdoId: ref?.id || null,
    dia,
    quantos,
    motivo: ref?.motivo || null,
    // Curto, para caber num chip ao lado do status — sem SUBSTITUIR o status.
    chip: dia ? `${nome} de ${dia} a corrigir` : `${quantos} diário(s) a corrigir`,
    // Longo, para o banner e o título do modal.
    titulo: dia ? `${nome} DE ${dia} REPROVADO` : 'RDO REPROVADO',
    botao: quantos > 1 ? `Corrigir (${quantos} reprovados)` : 'Corrigir este RDO',
  };
};

/**
 * OS ABERTA — os status em que o campo ainda pode lançar ou corrigir diário.
 *
 * Escrita num lugar só porque estava em TRÊS (card, banner e modal do app), todas dizendo
 * `status === 'in_progress'`, e por isso um diário reprovado numa OS PAUSADA ou numa OS já em
 * 'pending_approval' ficava invisível e sem botão de correção: o campo lia "reprovado" no sino
 * e não tinha por onde mexer. O backend nunca teve essa restrição — `update_rdo` e `create_rdo`
 * só recusam 'approved' e 'cancelled'.
 */
export const STATUS_OS_ABERTA = ['pending', 'in_progress', 'overdue', 'paused', 'pending_approval'];

export const osEstaAberta = (demanda) => STATUS_OS_ABERTA.includes(demanda?.status);

/**
 * A DEMANDA JA FOI INICIADA? — a pergunta que faltava, e que agora tres telas fazem.
 *
 * O dono, 2026-08-10: "no app do colaborador esta permitindo preencher o RDO sem ter iniciado a
 * demanda" e "o ideal e iniciar sempre pelo iniciar demanda".
 *
 * `osEstaAberta` inclui `pending` e `overdue`, entao o botao "Preencher RDO" aparecia ao lado do
 * "Iniciar Demanda", na OS que ninguem tinha comecado. E o backend AINDA disfarcava: mandar o
 * diario iniciava a OS por baixo, sem confirmar equipe, sem confirmar veiculo, sem `iniciada_em`
 * e sem as justificativas de ausencia que o iniciar exige. A OS chegava ao gestor como "Em
 * Andamento" com a equipe PLANEJADA — que pode nao ser a que foi.
 *
 * Iniciada = o campo passou pelo ritual de inicio (equipe confirmada, veiculo, ausencias
 * justificadas). `paused` e `pending_approval` entram porque ja passaram por ele.
 */
export const STATUS_OS_INICIADA = ['in_progress', 'paused', 'pending_approval'];

export const osIniciada = (demanda) => STATUS_OS_INICIADA.includes(demanda?.status);


/**
 * O ATRASO DESTA OS AINDA É DO CAMPO? — ou ele já entregou e a bola está com o escritório?
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * O RELATO — dono, 2026-08-20
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * *"As ordens de serviço constam como atrasada mesmo depois de ter sido feita pelos
 * colaboradores de campo. Fulano executou a OS, fez o RDO, mas a OS não foi finalizada, e pra
 * ele ainda fica constando que a OS está atrasada, e não quero que mostre, porque não
 * necessariamente vou colocar ele para finalizar a OS, porque trabalho com equipes bastante
 * rotativas."*
 *
 * A fórmula de atraso NÃO está errada: o prazo venceu mesmo e a OS não fechou. O que faltava era
 * um rótulo entre "Atrasada" e "Aprovada" — porque **fechar a OS é ato do gestor**
 * (`backend/routes/rdos_finalizacao.py`), e o diário do campo grava só `rdo_preenchido: true`.
 * O colaborador ficava olhando vermelho sem ter, do celular, ação nenhuma que o tirasse dali.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * ESTA FUNÇÃO NÃO SUBSTITUI `estaAtrasada` — ela responde OUTRA pergunta, EM CIMA dela
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * `estaAtrasada` (`utils/dataLocal.js`) é reexportada por `WeeklyView.helpers` e sustenta cinco
 * telas do GESTOR (`OsAtrasadasPainel`, `useProgramacao`, `WeeklyView.gantt`, `WeeklyView.list`)
 * mais o backend inteiro. Botar a pergunta do diário DENTRO dela desligaria o vermelho do gestor
 * em silêncio — e o dono precisa continuar vendo a OS atrasada, que é dele o ato de fechar.
 *
 * "Sim" = existe algo que o colaborador consegue fazer do celular para destravar.
 * "Não" = ele entregou tudo; quem tem de agir é o escritório.
 */
export const atrasoEDoCampo = (demanda) => {
  // ══════════════════════════════════════════════════════════════════════════════════════════
  // O CHECKLIST QUE NÃO ABRE VEM ANTES DE TUDO — e é o único caso que desliga até o "Corrigir RDO"
  // ══════════════════════════════════════════════════════════════════════════════════════════
  // `checklist_indisponivel` = o modelo foi APAGADO, ou ficou sem itens. O campo não tem AÇÃO
  // NENHUMA: o `POST /rdos` recusa
  // (`rdos_criacao.py:162 âncora: validar_checklists_demanda(rdo_input.demanda_id)`) e o
  // `PUT /rdos/{id}` — o "Corrigir RDO" do diário devolvido — recusa pela MESMA régua
  // (`rdos_edicao.py:120 âncora: validar_checklists_demanda(rdo.get("demanda_id"))`). Enviar,
  // corrigir e reenviar estão todos fechados; quem tem de agir é o gestor, trocando o modelo ou
  // tirando a exigência (`PUT /demandas/{id}/checklist-exigencia`).
  //
  // Sem esta linha a função devolvia `true` pelo `checklist_pendente` logo abaixo — pintando a
  // OS de vermelho "é com você" para quem não tem o que fazer, que é exatamente o relato do dono
  // que esta função existe para responder, com a causa trocada. É a MESMA classe corrigida na
  // Mesa (`services/mesa_checklist.py`) e na lista, na ponta do app de campo.
  //
  // `=== true` e não `!!`: ausente (app velho, servidor velho) é `undefined` e NÃO é resposta —
  // aqui não se inventa nem se apaga pendência a partir de um campo que não veio.
  if (demanda?.checklist_indisponivel === true) return false;
  // Diário devolvido: "Corrigir RDO" é ação dele, por mais que ele já tenha entregue antes.
  if (temDiarioRejeitado(demanda)) return true;
  // O checklist trava o envio do diário — enquanto pender, ele ainda tem o que fazer.
  if (demanda?.checklist_pendente === true) return true;
  // ENTREGOU? `rdos_total` entra junto porque `rdo_preenchido` é um booleano que nasceu depois:
  // documento legado pode ter os diários e não ter a flag, e perguntar pelos dois é o que faz a
  // regra valer para a OS de 2024 também.
  const entregou = demanda?.rdo_preenchido === true || (Number(demanda?.rdos_total) || 0) > 0;
  // NÃO PERGUNTO POR `osIniciada` AQUI, e a primeira versão perguntava — um teste a derrubou.
  // `overdue` não está em `STATUS_OS_INICIADA` (de propósito: aquela régua guarda o ritual de
  // "Iniciar demanda"), então uma OS carimbada `overdue` era classificada como "não começou" e
  // voltava a vermelho mesmo com diário entregue. E a pergunta era redundante: quem não iniciou
  // não tem diário, e cai no `return true` abaixo pelo caminho certo.
  return !entregou;
};

/**
 * ELE ENTREGOU; O QUE FALTA É ALGUÉM DO ESCRITÓRIO CLICAR.
 *
 * Só é verdade quando a OS está atrasada de fato — este é um rótulo MAIS GENTIL para um estado
 * que continua existindo, e nunca uma forma de esconder atraso que é do campo.
 */
export const aguardandoEscritorio = (demanda, hoje) => (
  estaAtrasada(demanda, hoje) && !atrasoEDoCampo(demanda)
);

/**
 * O CHECKLIST DESTA OS AINDA TEM ITEM POR RESPONDER?
 *
 * O dono: "nao permitir preencher o RDO se nao tiver o checklist preenchido inicialmente
 * (somente quando tiver checklist)". A ordem existe por um motivo pratico que ele explicou: o
 * RDO APROVEITA as descricoes e fotos do checklist — preencher o diario antes joga fora esse
 * trabalho.
 *
 * Sem checklist na OS, devolve false: a regra so vale para quem tem.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════
 * ISTO AQUI É UM APELIDO, e ele existe para NÃO haver duas respostas para uma pergunta
 * ════════════════════════════════════════════════════════════════════════════════════════
 *
 * A régua PRÓPRIA que morava aqui era `obrigatorio !== false && !concluido && !nao_aplica`, e
 * ela divergia da do backend em duas linhas — cada uma um relato do dono:
 *
 *   - exigia `concluido`, que a tela do RDO NÃO CONSEGUE ESCREVER (`ChecklistItemRDO` manda
 *     `fotos`, `resposta`, `observacao`, `nao_aplica` e `justificativa`, nunca `concluido`; o
 *     único escritor de `concluido` em todo o `src` é a `ChecklistPage`, em `/checklist/<id>`,
 *     que o perfil de campo toma 403 ao abrir);
 *   - tratava a AUSÊNCIA da chave `obrigatorio` como obrigação, enquanto a rota faz
 *     `item.get("obrigatorio", False)`.
 *
 * Medido por um cético (2026-08-12) sobre o MESMO documento que o campo consegue gravar:
 * `checklistPendente = true` × `checklistBarraDiario = false`. No `DemandaDetalhesModal` — a
 * tela que o dono abre ao tocar no card da OS — isso ESCONDIA o botão "Preencher RDO" e punha
 * no lugar "Responda o checklist acima para liberar o diário", para sempre.
 *
 * `pages/useRDOPage.regua` é a tradução linha a linha de `_validar_item_checklist`
 * (`backend/routes/checklists_common.py:70-149`), a mesma régua que `validar_checklists_demanda`
 * aplica no `POST /rdos` e que a home usa no banner. Uma pergunta, uma resposta.
 */
export { checklistBarraDiario as checklistPendente } from '../pages/useRDOPage.regua';

/** A rota que abre o diário reprovado — nunca o de hoje. */
export const rotaDiarioRejeitado = (demanda, extra = '') => {
  const id = demanda?.rdo_rejeitado_ref?.id;
  return id ? `/rdo/${demanda.id}?rdo=${id}${extra}` : `/rdo/${demanda.id}`;
};

import toast from 'react-hot-toast';

import { estaAtrasada } from './dataLocal';

const MAX_UPLOAD_BYTES = 500 * 1024 * 1024; // 500MB

/**
 * ISTO E UMA FOTO? — inclusive a do iPhone, que chega com MIME VAZIO.
 *
 * `''.startsWith('image/')` e FALSO, e era assim que tres telas recusavam a foto HEIC com
 * "Por favor, selecione uma imagem" — culpando o usuario por um formato que o navegador dele
 * simplesmente nao sabe nomear. O nome do arquivo e o unico dado que sobra quando o MIME falta.
 *
 * Uma regua so, porque a pergunta e a mesma em toda tela — e tres copias e como uma delas para
 * de crescer no dia em que entrar um formato novo.
 */
export const ehArquivoDeImagem = (file) => {
  const tipo = (file?.type || '').toLowerCase();
  if (tipo.startsWith('image/')) return true;
  return /\.(heic|heif|jpe?g|png|gif|webp|bmp)$/i.test(file?.name || '');
};

// Valida tamanho e comprime imagens antes do upload. HEIC/HEIF é convertido aqui quando o
// navegador sabe decodificá-lo (Safari/iOS); quando não sabe (Chrome), o arquivo ORIGINAL sobe e
// `backend/services/imagem_heic.py` converte na chegada.
// Retorna o File pronto para enviar, ou null se rejeitado (só por TAMANHO — ver o bloco no catch).
export const prepararArquivoUpload = async (file) => {
  if (!file) return null;
  if (file.size > MAX_UPLOAD_BYTES) {
    toast.error(`${file.name} muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Limite: 500MB`);
    return null;
  }
  const nome = (file.name || '').toLowerCase();
  const tipo = (file.type || '').toLowerCase();
  const ehHeic = tipo === 'image/heic' || tipo === 'image/heif' || nome.endsWith('.heic') || nome.endsWith('.heif');
  // HEIC/HEIF do iPhone: em vez de REJEITAR, CONVERTE para JPEG via canvas. O colaborador de
  // campo usa iPhone (câmera HEIC por padrão) e o Safari/iOS decodifica HEIC nativamente, então
  // compressImage já reencoda. Só cai no aviso se o canvas NÃO conseguir decodificar (ex.:
  // Android/Chrome sem suporte a HEIC) — antes o app rejeitava toda foto HEIC de cara.
  if (ehHeic || tipo.startsWith('image/')) {
    try {
      const compressed = await compressImage(file);
      const blob = await (await fetch(compressed.data)).blob();
      // Os bytes saem JPEG (canvas.toBlob 'image/jpeg'): o NOME também vira .jpg — qualquer
      // extensão (.heic/.png/…) é trocada, ou .jpg é anexado se não houver extensão.
      const nomeJpeg = (file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
      return new window.File([blob], nomeJpeg, { type: 'image/jpeg' });
    } catch (err) {
      console.error('Erro na compressão/conversão:', err);
      // ════════════════════════════════════════════════════════════════════════════════════════
      // O HEIC QUE O CANVAS NÃO DECODIFICA SOBE ASSIM MESMO — o SERVIDOR converte
      // ════════════════════════════════════════════════════════════════════════════════════════
      // Dono, 2026-08-20: *"os colaboradores de campo não estão conseguindo anexar fotos heic"*.
      //
      // Este `catch` RECUSAVA a foto. E ele dispara sempre no Chrome (Android e desktop): o
      // canvas só desenha o que o navegador sabe DECODIFICAR, e Chrome não decodifica HEIC. No
      // iPhone funcionava, porque o Safari decodifica — daí o defeito atingir uns e outros não.
      //
      // Agora o arquivo ORIGINAL segue para o upload, e `backend/services/imagem_heic.py` o
      // converte para JPEG na chegada, pelos dois caminhos de upload. O que fica guardado é um
      // JPEG de verdade, com nome `.jpg` — galeria, PDF e os filtros de imagem por extensão
      // passam a funcionar sem saber que HEIC existiu.
      //
      // O AVISO SUMIU DE PROPÓSITO: ele mandava o colaborador mexer em Ajustes do iPhone para
      // resolver um problema que não é dele. A foto sobe; se a conversão falhar no servidor, o
      // arquivo é guardado como veio e a foto NÃO se perde — perder trabalho de campo é o único
      // desfecho que não se recupera, porque a pessoa já desceu do telhado.
      if (ehHeic) return file;
    }
  }
  return file;
};

// Função para comprimir imagem
export const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Compressão falhou'));
              return;
            }
            
            const compressedReader = new FileReader();
            compressedReader.onloadend = () => {
              resolve({
                data: compressedReader.result,
                size: blob.size
              });
            };
            compressedReader.readAsDataURL(blob);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Função para download de arquivo (suporta base64 e URL)
export const downloadFile = (src, fileName) => {
  try {
    if (src.startsWith('data:')) {
      // Base64 data URL
      const [header, base64Data] = src.split(',');
      const mimeMatch = header.match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // `fetch` só REJEITA em falha de REDE: 403/404/500 RESOLVEM normalmente. Sem checar
      // `r.ok`, o corpo do erro virava o "arquivo" — o anexo do RDO baixava como .jpg contendo
      // {"detail":"Acesso não autorizado ao arquivo"}, e como não houve rejeição o `.catch`
      // nunca rodava: nenhuma mensagem, só um arquivo que não abre. Era isto que aparecia como
      // "a foto do RDO não baixa" / "não consigo baixar o PDF aprovado".
      // `credentials: 'include'` garante o cookie HttpOnly de sessão — o proxy de arquivos exige
      // sessão OU URL assinada (STORAGE_REQUIRE_AUTH=true em produção).
      fetch(src, { credentials: 'include' })
        .then(r => {
          if (!r.ok) { const e = new Error(`HTTP ${r.status}`); e.status = r.status; throw e; }
          return r.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch((err) => {
          console.error('Download do anexo falhou:', src, err);
          // Resposta do servidor (4xx/5xx): abrir de novo daria o MESMO erro, então diz o que
          // houve em vez de fingir. Só falha de rede justifica a 2ª tentativa por navegação.
          if (err?.status) {
            toast.error(err.status === 403 || err.status === 401
              ? 'Sem permissão para baixar este arquivo. Faça login de novo e tente outra vez.'
              : err.status === 404
                ? 'Arquivo não encontrado no servidor — pode não ter subido no envio do RDO.'
                : `Não foi possível baixar o arquivo (erro ${err.status}).`);
            return;
          }
          const a = document.createElement('a');
          a.href = src;
          a.download = fileName;
          a.target = '_blank';
          a.click();
        });
    }
  } catch (err) {
    console.error('Erro no download:', err);
    toast.error('Erro ao baixar arquivo');
  }
};

// Função para abrir PDF (suporta base64 e URL)
export const openPDF = (src) => {
  try {
    if (src.startsWith('data:')) {
      const [header, base64Data] = src.split(',');
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } else {
      // Server URL — abre a aba JÁ no gesto do clique (evita bloqueio de popup no
      // WebView do celular) e troca para o blob após o fetch same-origin; se o fetch
      // falhar, cai para a navegação direta.
      const win = window.open('', '_blank');
      // Mesmo defeito do downloadFile: sem checar `r.ok`, um 403 virava "PDF" com o JSON de erro
      // dentro — aba em branco, sem explicação. Ver o comentário lá para o porquê do `credentials`.
      fetch(src, { credentials: 'include' })
        .then(r => {
          if (!r.ok) { const e = new Error(`HTTP ${r.status}`); e.status = r.status; throw e; }
          return r.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          if (win) { win.location = url; } else { window.open(url, '_blank'); }
        })
        .catch((err) => {
          console.error('Abertura do PDF falhou:', src, err);
          if (err?.status) {          // erro do servidor: reabrir daria o mesmo
            if (win) win.close();     // não deixar aba em branco para o usuário fechar
            toast.error(err.status === 403 || err.status === 401
              ? 'Sem permissão para abrir este arquivo. Faça login de novo e tente outra vez.'
              : err.status === 404
                ? 'Arquivo não encontrado no servidor.'
                : `Não foi possível abrir o arquivo (erro ${err.status}).`);
            return;
          }
          if (win) { win.location = src; } else { window.open(src, '_blank'); }
        });
    }
  } catch (err) {
    console.error('Erro ao abrir PDF:', err);
    toast.error('Erro ao abrir PDF');
  }
};
